import { useMemo } from 'react';
import { type CombinedError, useQuery } from 'urql';

import {
  WEEK_SELECTION_LIST_QUERY,
  type WeekSelectionListQuery,
} from '@revelio/data-access';

type WeekRefEntry = { id: string; shortName: string };

/**
 * Maps weekly point ids (the `id` on each `postings_v3` data point) to the
 * Monday `yyyy-MM-dd` date of that week. The backend returns the week id as a
 * string, coerced to a number key for lookup.
 */
export const buildWeekRefs = (
  week: ReadonlyArray<WeekRefEntry>
): Map<number, string> => {
  const refs = new Map<number, string>();
  for (const { id, shortName } of week) {
    const numericId = Number(id);
    if (Number.isNaN(numericId)) continue;
    refs.set(numericId, shortName);
  }
  return refs;
};

export const usePostingsV3WeekRefs = ({
  pause = false,
}: {
  pause?: boolean;
} = {}): {
  weekRefs: Map<number, string>;
  isLoading: boolean;
  error: CombinedError | undefined;
} => {
  const [{ data, fetching, error }] = useQuery<WeekSelectionListQuery>({
    query: WEEK_SELECTION_LIST_QUERY,
    // Only the postings_v3 converters need this; skip it on the flag-off path.
    pause,
  });

  const weekRefs = useMemo(() => {
    const week = data?.selectionList?.week ?? [];
    const entries = week.filter(
      (w): w is WeekRefEntry =>
        w != null && typeof w.id === 'string' && typeof w.shortName === 'string'
    );
    return buildWeekRefs(entries);
  }, [data]);

  return { weekRefs, isLoading: fetching, error };
};
